import React from 'react';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Mail, Paperclip, Circle, Loader2 } from 'lucide-react';
import { GmailMessage } from '../../types/api';
import { SkeletonLoader } from '../SkeletonLoader';

interface MessageListProps {
  messages: GmailMessage[];
  selectedMessageId: string | null;
  onSelectMessage: (messageId: string) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${props => props.theme.colors.backgroundSecondary};
`;

const Header = styled.div`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[2]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const HeaderCounter = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
`;

const MessageListWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
  min-height: 0;
  
  /* Smooth scrolling */
  scroll-behavior: smooth;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundSecondary};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borders.radius.full};
    
    &:hover {
      background: ${props => props.theme.colors.textTertiary};
    }
  }
`;

const MessageCard = styled.button<{ $active: boolean; $unread: boolean }>`
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  background: ${props => props.$active
    ? props.theme.colors.primary + '12'
    : props.$unread
      ? props.theme.colors.backgroundTertiary
      : props.theme.colors.backgroundSecondary};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    gap: ${props => props.theme.spacing[2]};
  }
`;

const MessageMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
  min-width: 0;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing[2]};
  min-width: 0;
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  min-width: 0;

  .from {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.textPrimary};
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    .from {
      max-width: 200px;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    .from {
      max-width: 100%;
    }
  }
`;

const MessageActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  flex-shrink: 0;
`;

const SubjectRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  min-width: 0;
  width: 100%; /* Ensure full width */

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0; /* Reduce gap on mobile */
  }
`;

const Subject = styled.span<{ $unread: boolean }>`
  flex: 0 0 auto; /* Don't shrink, auto width based on content up to max */
  max-width: 60%; /* Cap width */
  min-width: 0;
  font-size: 13px;
  font-weight: ${props => props.$unread
    ? props.theme.typography.fontWeight.semibold
    : props.theme.typography.fontWeight.medium};
  color: ${props => props.$unread
    ? props.theme.colors.textPrimary
    : props.theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    max-width: 100%;
  }
`;

const Snippet = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  color: ${props => props.theme.colors.textTertiary};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    display: block; /* Ensure it takes full width on new line */
  }

  &::before {
    content: '•';
    color: ${props => props.theme.colors.textSecondary};
    @media (max-width: ${props => props.theme.breakpoints.md}) {
      display: none; /* Hide separator on mobile */
    }
  }
`;

const Time = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
`;

const AttachmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borders.radius.sm};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[6]};
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    opacity: 0.4;
  }
`;

const LoadMoreButton = styled.button`
  margin: ${props => props.theme.spacing[1]} auto 0;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader2)`
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const formatWhen = (timestamp?: number) => {
  if (!timestamp) return 'Agora';
  try {
    return formatDistanceToNow(new Date(Number(timestamp)), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch (error) {
    console.error('[MessageList] Failed to format date:', error);
    return '—';
  }
};

const hasAttachments = (message: GmailMessage) =>
  Array.isArray(message.attachments) && message.attachments.length > 0;

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  selectedMessageId,
  onSelectMessage,
  isLoading = false,
  isLoadingMore = false,
  onLoadMore,
  hasMore = false,
}) => {
  const renderSkeletons = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <SkeletonLoader key={index} height={64} borderRadius="18px" />
    ));

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <Mail size={18} />
          Mensagens
        </HeaderTitle>
        <HeaderCounter>{messages.length} resultados</HeaderCounter>
      </Header>

      <MessageListWrapper>
        {isLoading ? (
          renderSkeletons()
        ) : messages.length === 0 ? (
          <EmptyState>
            <Mail />
            <div>Nenhuma mensagem encontrada para esta label.</div>
          </EmptyState>
        ) : (
          messages.map(message => {
            const unread = Boolean(message.labelIds?.includes('UNREAD'));
            return (
              <MessageCard
                key={message.id}
                type="button"
                $active={selectedMessageId === message.id}
                $unread={unread}
                onClick={() => onSelectMessage(message.id)}
              >
                <MessageMain>
                  <MessageHeader>
                    <MessageMeta>
                      {unread && <Circle size={8} fill="#ef4444" color="#ef4444" />}
                      <span className="from">{message.from || 'Remetente desconhecido'}</span>
                    </MessageMeta>
                    <MessageActions>
                      {hasAttachments(message) && (
                        <AttachmentBadge>
                          <Paperclip size={12} />
                          {message.attachments?.length}
                        </AttachmentBadge>
                      )}
                      <Time>{formatWhen(message.internalDate)}</Time>
                    </MessageActions>
                  </MessageHeader>
                  <SubjectRow>
                    <Subject $unread={unread}>
                      {message.subject || '(sem assunto)'}
                    </Subject>
                    <Snippet>{message.snippet || 'Sem prévia disponível.'}</Snippet>
                  </SubjectRow>
                </MessageMain>
              </MessageCard>
            );
          })
        )}

        {hasMore && !isLoading && messages.length > 0 && (
          <LoadMoreButton
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Spinner size={14} />
                Carregando...
              </span>
            ) : (
              'Carregar mais'
            )}
          </LoadMoreButton>
        )}
      </MessageListWrapper>
    </Container>
  );
};
